import { useQuery } from 'react-query';

import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { ShowQueryErrorType, ShowQueryOpts } from '../useShowQuery';
import { useShowQueryUpdateItemCache } from '../useShowQuery/hooks/useShowQueryUpdateItemCache';

import {
  FetchCacheItemSources,
  FetchItemCacheKey,
  FetchItemGqlQuery,
  ID,
  NanoID,
  UUID
} from '../../../../../types';
import { PermissionAction } from '../../../../permissions/permissionsTypes';

import { fetchCacheItem } from '../../actions/fetchCacheItem';
import { fetchReadItem } from '../../actions/fetchReadItem';

import { LocalForage } from '../../../../../utils/LocalForage';
import { parseRequestError } from '../../../../../utils/parseRequestError';

import { CommonPermissions } from '../../../commonConstants';

interface CacheShowQueryOptions<ResponseType> {
  action?: PermissionAction;
  additionalVariables?: Record<string, unknown>;
  cacheKey?: FetchItemCacheKey;
  cacheQuery?: FetchItemGqlQuery;
  fullCacheKey?: FetchItemCacheKey;
  itemKey: string;
  options: ShowQueryOpts<ResponseType>;
  placeholderData?: ResponseType;
  query: FetchItemGqlQuery;
  source?: FetchCacheItemSources;
  uuid: UUID | NanoID | ID;
  useCacheApiOnly?: boolean;
}

function useCacheShowQuery<ResponseType, ItemType>({
  action,
  additionalVariables,
  cacheKey,
  query,
  cacheQuery,
  fullCacheKey,
  itemKey,
  options = {},
  placeholderData,
  source,
  uuid,
  useCacheApiOnly = false
}: CacheShowQueryOptions<ResponseType>) {
  const currentUser = useCurrentUser();
  const localForageCacheKey = fullCacheKey || `${cacheKey}-${uuid}`;
  const withCache =
    useCacheApiOnly ||
    (action &&
      currentUser?.hasPermissions(action) &&
      currentUser.hasPermissions(CommonPermissions.READ_CACHE_QUERY));

  const {
    data: localForagePlaceholderData,
    isFetched: placeholderDataFetched
  } = useQuery<(ResponseType & { isOptimistic?: boolean }) | null>(
    `${localForageCacheKey}-placeholder`,
    () => LocalForage.getItem<ResponseType>(localForageCacheKey),
    {
      enabled: options.enabledPlaceholder,
      onSuccess: (data) => options.onSuccess?.(data)
    }
  );

  const {
    data,
    isFetched,
    isLoading,
    isFetching,
    error,
    isPlaceholderData,
    refetch,
    dataUpdatedAt
  } = useQuery<ResponseType & { isOptimistic?: boolean }, ShowQueryErrorType>(
    fullCacheKey || [cacheKey, uuid],
    () =>
      withCache
        ? fetchCacheItem({
            additionalVariables,
            query: cacheQuery,
            uuid,
            source
          })
        : fetchReadItem({
            query,
            uuid
          }),
    {
      cacheTime: options.cacheTime,
      staleTime: options.staleTime,
      enabled:
        options.enabled ||
        (options.enabledPlaceholder && placeholderDataFetched),
      placeholderData:
        placeholderData || localForagePlaceholderData || undefined,
      onSettled: options.onSettled,
      onSuccess: (data) => {
        options.onSuccess?.(data);
        return LocalForage.setItem<ResponseType>(localForageCacheKey, data);
      },
      onError: options.onError
    }
  );

  const updateCacheShowQueryItemCache = useShowQueryUpdateItemCache<
    ResponseType,
    ItemType
  >({
    fullCacheKey: fullCacheKey || [cacheKey, uuid],
    itemKey
  });

  const item: ItemType | null = data?.[itemKey] || null;

  return {
    isOptimistic: !!data?.isOptimistic,
    item,
    itemError: parseRequestError(error),
    itemErrorData: error,
    itemFetched: isFetched,
    itemFetching: isFetching,
    itemIsPlaceholderData: isPlaceholderData,
    itemLoading: isLoading,
    refetchItem: refetch,
    updateCacheShowQueryItemCache,
    dataUpdatedAt
  };
}

export default useCacheShowQuery;
